import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useStockMovementStore, PendingAction } from '../../../store/stockMovementStore';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { colours } from './shared';

interface Props {
  onClose: () => void;
  onCommitted: () => void;
}

const LABELS: Record<PendingAction['type'], string> = {
  add_stock:    'Add Stock',
  return_stock: 'Return Stock',
  transfer:     'Transfer',
  check_out:    'Check Out',
};

const TYPE_COLOURS: Record<PendingAction['type'], string> = {
  add_stock:    '#16a34a',
  return_stock: '#2563eb',
  transfer:     '#9333ea',
  check_out:    '#d97706',
};

const ReviewCommitPanel: React.FC<Props> = ({ onClose, onCommitted }) => {
  const { pendingActions, removePending, commitAll, isCommitting } = useStockMovementStore();
  const { items, stockUnits }       = useInventoryStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();

  const [confirmMode, setConfirmMode] = useState(false);

  const itemById  = new Map(items.map(i => [i.id, i]));
  const unitById  = new Map(stockUnits.map(u => [u.id, u]));
  const whById    = new Map(warehouses.map(w => [w.id, w]));

  const areaName = (warehouseId: string, areaId: string | null) => {
    if (!areaId) return null;
    const areas = storageAreasByWarehouse[warehouseId] ?? [];
    return areas.find(a => a.id === areaId)?.name ?? null;
  };

  const totalActions = pendingActions.length;
  const totalUnits = pendingActions.reduce((sum, a) => {
    if (a.type === 'add_stock') return sum + a.rows.reduce((s, r) => s + r.quantity, 0);
    return sum + (a as { unitIds: string[] }).unitIds?.length ?? 0;
  }, 0);

  const handleCommit = async () => {
    try {
      const result = await commitAll();
      onCommitted();
      if (result.success) onClose();
      else setConfirmMode(false);
    } catch {
      setConfirmMode(false);
    }
  };

  const { border, cardBg, muted, text, headerBg } = colours;

  return (
    // Backdrop
    <div
      className="absolute inset-0 z-40 flex justify-end"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Panel */}
      <div
        className="flex flex-col h-full w-full max-w-lg overflow-hidden"
        style={{ backgroundColor: colours.pageBg, borderLeft: `1px solid ${border}` }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{ backgroundColor: cardBg, borderBottom: `1px solid ${border}` }}
        >
          <div>
            <h2 className="text-sm font-semibold" style={{ color: text }}>Review &amp; Commit</h2>
            <p className="text-sm mt-0.5" style={{ color: muted }}>
              {totalActions} action{totalActions > 1 ? 's' : ''} · {totalUnits} unit{totalUnits > 1 ? 's' : ''} affected
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: muted }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = colours.rowHover)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Action list */}
        <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-3">
          {pendingActions.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-sm" style={{ color: muted }}>No pending actions.</p>
            </div>
          )}

          {pendingActions.map(action => (
            <div
              key={action.id}
              className="rounded overflow-hidden"
              style={{ border: `1px solid ${border}`, backgroundColor: cardBg }}
            >
              {/* Action header */}
              <div
                className="flex items-center justify-between px-3 py-2"
                style={{ borderBottom: `1px solid ${border}`, backgroundColor: headerBg }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: TYPE_COLOURS[action.type] }}
                  />
                  <span className="text-sm font-semibold" style={{ color: text }}>{LABELS[action.type]}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removePending(action.id)}
                  className="p-1 rounded transition-colors hover:bg-red-900/30"
                  style={{ color: muted }}
                  title="Remove this action"
                >
                  <Trash2 size={12} />
                </button>
              </div>

              {/* Action body */}
              <div className="px-3 py-2 text-sm flex flex-col gap-1" style={{ color: text }}>

                {/* Add Stock */}
                {action.type === 'add_stock' && (() => {
                  const wh   = whById.get(action.warehouseId);
                  const area = areaName(action.warehouseId, action.storageAreaId);
                  return (
                    <>
                      <div style={{ color: muted }}>
                        → {wh?.name ?? '—'}{area ? ` / ${area}` : ''}
                      </div>
                      {action.rows.map((row, i) => {
                        const item = itemById.get(row.itemId);
                        const idents = (row.units ?? [])
                          .map(u => [u.serial, u.mac].filter(Boolean).join(' / '))
                          .filter(Boolean);
                        return (
                          <div key={i} className="pl-2 flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium">{item?.name ?? 'Unknown item'}</span>
                            <span style={{ color: muted }}>× {row.quantity}</span>
                            <span className="capitalize" style={{ color: muted }}>({row.condition})</span>
                            {row.reference && <span style={{ color: muted }}>· {row.reference}</span>}
                            {idents.length > 0 && (
                              <span style={{ color: muted }}>· {idents.slice(0, 3).join(', ')}{idents.length > 3 ? '…' : ''}</span>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}

                {/* Return Stock */}
                {action.type === 'return_stock' && (() => {
                  const wh   = whById.get(action.warehouseId);
                  const area = areaName(action.warehouseId, action.storageAreaId);
                  return (
                    <>
                      <div style={{ color: muted }}>→ {wh?.name ?? '—'}{area ? ` / ${area}` : ''} · <span className="capitalize">{action.condition}</span></div>
                      {action.unitIds.map(uid => {
                        const unit = unitById.get(uid);
                        const item = unit ? itemById.get(unit.itemId) : null;
                        return (
                          <div key={uid} className="pl-2 flex items-center gap-1.5">
                            <span className="font-medium">{item?.name ?? '—'}</span>
                            <span style={{ color: muted }}>#{unit?.unitId ?? '?'}</span>
                            {unit?.serialMac && <span style={{ color: muted }}>· {unit.serialMac}</span>}
                          </div>
                        );
                      })}
                      {action.reference && <div style={{ color: muted }}>ref: {action.reference}</div>}
                    </>
                  );
                })()}

                {/* Transfer */}
                {action.type === 'transfer' && (() => {
                  const wh   = whById.get(action.toWarehouseId);
                  const area = areaName(action.toWarehouseId, action.toStorageAreaId);
                  return (
                    <>
                      <div style={{ color: muted }}>→ {wh?.name ?? '—'}{area ? ` / ${area}` : ''}</div>
                      {action.unitIds.map(uid => {
                        const unit = unitById.get(uid);
                        const item = unit ? itemById.get(unit.itemId) : null;
                        const fromWh = unit ? whById.get(unit.warehouseId) : null;
                        const fromAreas = unit ? (storageAreasByWarehouse[unit.warehouseId] ?? []) : [];
                        const fromArea = unit?.storageAreaId ? fromAreas.find(a => a.id === unit.storageAreaId) : null;
                        return (
                          <div key={uid} className="pl-2 flex items-center gap-1.5">
                            <span className="font-medium">{item?.name ?? '—'}</span>
                            <span style={{ color: muted }}>#{unit?.unitId ?? '?'}</span>
                            <span style={{ color: '#4b5563' }}>
                              from {fromWh?.name ?? '—'}{fromArea ? ` / ${fromArea.name}` : ''}
                            </span>
                          </div>
                        );
                      })}
                      {action.reference && <div style={{ color: muted }}>ref: {action.reference}</div>}
                    </>
                  );
                })()}

                {/* Check Out */}
                {action.type === 'check_out' && (
                  <>
                    <div style={{ color: muted }}>→ {action.owner || 'Unknown'}</div>
                    {action.unitIds.map(uid => {
                      const unit = unitById.get(uid);
                      const item = unit ? itemById.get(unit.itemId) : null;
                      return (
                        <div key={uid} className="pl-2 flex items-center gap-1.5">
                          <span className="font-medium">{item?.name ?? '—'}</span>
                          <span style={{ color: muted }}>#{unit?.unitId ?? '?'}</span>
                          {unit?.serialMac && <span style={{ color: muted }}>· {unit.serialMac}</span>}
                        </div>
                      );
                    })}
                    {action.reference && <div style={{ color: muted }}>ref: {action.reference}</div>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {pendingActions.length > 0 && (
          <div
            className="flex-shrink-0 px-5 py-3 flex flex-col gap-2"
            style={{ borderTop: `1px solid ${border}`, backgroundColor: cardBg }}
          >
            {!confirmMode ? (
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center gap-1.5 flex-1 rounded px-3 py-2 text-sm"
                  style={{ backgroundColor: '#451a0320', border: '1px solid #92400e', color: '#fbbf24' }}
                >
                  <AlertTriangle size={13} />
                  This will permanently write all actions to the database.
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmMode(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-semibold transition-colors"
                  style={{ backgroundColor: '#16a34a', color: '#fff' }}
                >
                  <CheckCircle size={13} />
                  Commit All
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-center font-medium" style={{ color: '#fbbf24' }}>
                  Are you sure? This cannot be undone.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmMode(false)}
                    className="flex-1 py-2 rounded text-sm border transition-colors"
                    style={{ borderColor: border, color: muted }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCommit}
                    disabled={isCommitting}
                    className="flex-1 py-2 rounded text-sm font-semibold transition-colors disabled:opacity-50"
                    style={{ backgroundColor: '#16a34a', color: '#fff' }}
                  >
                    {isCommitting ? 'Committing…' : 'Yes, Commit All'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCommitPanel;
